import React, { useEffect, useCallback, ComponentType } from "react";
import { X } from "lucide-react";

interface TwoStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: 1 | 2;
  isSubmitting?: boolean;
  icon: ComponentType<{ className?: string }>;
  step1Title: string;
  step2Title: string;
  step1Content: React.ReactNode;
  step2Content: React.ReactNode;
  step1Actions: React.ReactNode;
  step2Actions: React.ReactNode;
  maxWidth?: "sm" | "md" | "lg";
}

const maxWidthClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
};

export function TwoStepModal({
  isOpen,
  onClose,
  step,
  isSubmitting = false,
  icon: Icon,
  step1Title,
  step2Title,
  step1Content,
  step2Content,
  step1Actions,
  step2Actions,
  maxWidth = "md",
}: TwoStepModalProps) {
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSubmitting, handleClose]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const currentTitle = step === 1 ? step1Title : step2Title;
  const currentContent = step === 1 ? step1Content : step2Content;
  const currentActions = step === 1 ? step1Actions : step2Actions;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className={`bg-white dark:bg-gray-800 rounded-xl p-6 w-full ${maxWidthClasses[maxWidth]} shadow-xl`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Icon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {currentTitle}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          {currentContent}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          {currentActions}
        </div>
      </div>
    </div>
  );
}